import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import { FiArrowLeft, FiShield, FiDownload, FiCheckCircle, FiXCircle, FiAlertCircle } from 'react-icons/fi';

const VPNPage: React.FC = () => {
  const navigate = useNavigate();
  const [vpnStatus, setVpnStatus] = useState<'connected' | 'disconnected'>('disconnected');

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Navigation */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/home')}
            className="flex items-center text-uc-navy hover:text-uc-gold transition-colors mb-4"
          >
            <FiArrowLeft size={20} className="mr-2" />
            Back to Home
          </button>
          <h1 className="text-3xl font-bold text-uc-navy font-header">UC MERCED VPN</h1>
          <p className="text-gray-600 mt-2">Secure access to campus network resources</p>
        </div>

        {/* VPN Status */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <FiShield className="text-uc-gold" size={32} />
            <h2 className="text-xl font-bold text-uc-navy">VPN Status</h2>
          </div>

          <div className="flex items-center justify-between p-6 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-4">
              {vpnStatus === 'connected' ? (
                <>
                  <FiCheckCircle className="text-green-600" size={40} />
                  <div>
                    <p className="font-bold text-green-900 text-lg">Connected</p>
                    <p className="text-sm text-green-700">Secure connection established</p>
                  </div>
                </>
              ) : (
                <>
                  <FiXCircle className="text-red-600" size={40} />
                  <div>
                    <p className="font-bold text-red-900 text-lg">Disconnected</p>
                    <p className="text-sm text-red-700">Not connected to UC Merced VPN</p>
                  </div>
                </>
              )}
            </div>
            <button
              onClick={() => setVpnStatus(vpnStatus === 'connected' ? 'disconnected' : 'connected')}
              className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                vpnStatus === 'connected'
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              {vpnStatus === 'connected' ? 'Disconnect' : 'Connect'}
            </button>
          </div>
        </div>

        {/* Download VPN Client */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold text-uc-navy mb-4">Download VPN Client</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button className="flex flex-col items-center gap-3 p-6 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <FiDownload className="text-uc-gold" size={32} />
              <div className="text-center">
                <p className="font-semibold text-uc-navy">Windows</p>
                <p className="text-sm text-gray-600">Download for Windows</p>
              </div>
            </button>
            <button className="flex flex-col items-center gap-3 p-6 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <FiDownload className="text-uc-gold" size={32} />
              <div className="text-center">
                <p className="font-semibold text-uc-navy">macOS</p>
                <p className="text-sm text-gray-600">Download for Mac</p>
              </div>
            </button>
            <button className="flex flex-col items-center gap-3 p-6 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <FiDownload className="text-uc-gold" size={32} />
              <div className="text-center">
                <p className="font-semibold text-uc-navy">Linux</p>
                <p className="text-sm text-gray-600">Download for Linux</p>
              </div>
            </button>
          </div>
        </div>

        {/* Setup Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <div className="flex items-start gap-3">
            <FiAlertCircle className="text-blue-600 mt-1 flex-shrink-0" size={24} />
            <div>
              <h3 className="font-bold text-blue-900 mb-2">Setup Instructions</h3>
              <ol className="list-decimal list-inside space-y-2 text-blue-800 text-sm">
                <li>Download and install the VPN client for your operating system</li>
                <li>Launch the VPN application</li>
                <li>Enter the server address: <span className="font-mono bg-blue-100 px-2 py-1 rounded">vpn.ucmerced.edu</span></li>
                <li>Use your UCMercedID and password to connect</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-bold text-uc-navy mb-4">About UC Merced VPN</h3>
          <div className="space-y-3 text-gray-700">
            <p>
              The UC Merced VPN (Virtual Private Network) allows you to securely access campus network resources from off-campus. Features include:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Secure encrypted connection to campus network</li>
              <li>Access to library resources and databases</li>
              <li>Connect to campus file servers and shared drives</li>
              <li>Access to administrative systems</li>
              <li>Remote desktop access to campus computers</li>
              <li>Compatible with Windows, macOS, and Linux</li>
            </ul>
            <p className="text-sm text-gray-600 mt-4">
              For technical support or connection issues, contact IT Support at it@ucmerced.edu or call (209) 228-HELP (4357).
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default VPNPage;
